import React, { useState } from "react";
import {
    Elements,
    useStripe,
    useElements,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    AddressElement,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogBody,
    DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { toast } from "components/ui/toast";
import { CreditCard, Lock, Shield } from "lucide-react";

import { ACCEPTED_CARDS_CONFIG } from "../cardsConfig";

import VisaIcon from "assets/credit-card/Visa.svg";
import MasterCardIcon from "assets/credit-card/mastercard-old.svg";
import AmexIcon from "assets/credit-card/AmericanExpress.svg";
import DiscoverIcon from "assets/credit-card/Discover.svg";
import DinersIcon from "assets/credit-card/DinersClub.svg";
import SimpleCardIcon from "assets/credit-card/SimpleCard.svg";
import CardErrorIcon from "assets/credit-card/CardError.svg";
import { useAddStripeCard } from "core";
import { useTheme } from "contexts/ThemeContext";

// ---- Stripe public key ----
// NOTE: For card elements to work on a live domain (non-localhost), you MUST use a
// pk_live_ key. Using pk_test_ keys blocks Stripe elements on non-localhost domains.
const STRIPE_KEY = process.env.REACT_APP_stripe_api_token;
if (STRIPE_KEY?.startsWith("pk_test_") && window.location.hostname !== "localhost") {
    console.error(
        "[Stripe] ⚠️  You are using a TEST key on a non-localhost domain.\n" +
        "Stripe card elements will NOT be interactive on live domains with pk_test_ keys.\n" +
        "Please set REACT_APP_stripe_api_token=pk_live_<your_key> in .env.prod"
    );
}
const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;

interface AddPaymentMethodModalProps {
    showModal: boolean;
    onClose: (value?: any) => void;
}

// ---- Map detected brands to icons ----
const CARD_BRAND_ICONS: Record<string, string> = {
    Visa: VisaIcon,
    MasterCard: MasterCardIcon,
    "American Express": AmexIcon,
    Discover: DiscoverIcon,
    "Diners Club": DinersIcon,
    SimpleCard: SimpleCardIcon,
    CardError: CardErrorIcon,
};

const _getCardIcon = (brand: string) => CARD_BRAND_ICONS[brand] || SimpleCardIcon;

// ---- Main Inner Component ----
const AddPaymentMethodInner: React.FC<AddPaymentMethodModalProps> = ({
    showModal,
    onClose,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [_cardBrand, setCardBrand] = useState("SimpleCard");
    const [loading, setLoading] = useState(false);
    const [focusedField, setFocusedField] = useState<string | null>(null);

    const { mutateAsync: addStripeCardMutation } = useAddStripeCard();
    const { isDark } = useTheme();

    const elementStyles = {
        style: {
            base: {
                fontSize: "14px",
                color: isDark ? "#f3f4f6" : "#1f2937",
                fontFamily: "'Inter', system-ui, sans-serif",
                fontWeight: "400",
                "::placeholder": { color: isDark ? "#6b7280" : "#9ca3af" },
                ":-webkit-autofill": { color: isDark ? "#f3f4f6" : "#1f2937" },
            },
            invalid: { color: "#ef4444", iconColor: "#ef4444" },
        },
    };

    const getCardBrand = (detectedBrand: string) => {
        const map: Record<string, string> = {
            visa: "Visa", mastercard: "MasterCard", amex: "American Express",
            discover: "Discover", diners: "Diners Club", jcb: "JCB", unionpay: "UnionPay",
        };
        return map[detectedBrand] || "SimpleCard";
    };

    const handleCardChange = (event: any) => {
        if (event.error?.code === "invalid_number" || event.error?.code === "incomplete_number") {
            setCardBrand("CardError");
        } else if (event.brand) {
            setCardBrand(event.brand === "unknown" ? "SimpleCard" : getCardBrand(event.brand));
        }
    };

    const createStripeCardToken = async () => {
        if (!stripe || !elements) return;

        const cardElement = elements.getElement(CardNumberElement);
        const addressElement = elements.getElement(AddressElement);
        if (!cardElement || !addressElement) return;
        const { complete, value } = await addressElement.getValue();

        if (!complete) {
            toast.error("Please fill in all billing address fields.");
            return;
        }

        const tokenData: Record<string, string> = {
            name: value.name || "",
            address_line1: value.address.line1 || "",
            address_city: value.address.city || "",
            address_zip: value.address.postal_code || "",
            address_country: value.address.country || "",
        };
        if (value.address.line2) tokenData.address_line2 = value.address.line2;
        if (value.address.state) tokenData.address_state = value.address.state;

        setLoading(true);
        const result = await stripe.createToken(cardElement as any, tokenData);

        if (result.error) {
            toast.error(result.error.message || "Error creating token");
            setLoading(false);
            return;
        }
        if (result.token) addStripeCard(result.token);
    };

    const addStripeCard = async (token: any) => {
        try {
            setLoading(true);
            await addStripeCardMutation({
                methodType: "card",
                stripeTokenId: token.id,
                defaultMethod: true,
            });
            toast.success("Card added successfully");
            onClose(false);
        } catch {
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const stripeFocusCls = (field: string) =>
        `rounded-lg border shadow-sm transition-all duration-200  dark:bg-gray-800 p-3 ${focusedField === field
            ? "border-blue-600 ring-1 ring-blue-600 dark:border-blue-500 dark:ring-blue-500"
            : "border-line-strong dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600"
        }`;

    return (
        <Dialog open={showModal} onOpenChange={onClose}>
            <DialogContent className="w-[min(520px,95vw)] max-w-none p-0 overflow-hidden" showClose={false}>
                {/* Screen reader accessibility */}
                <DialogTitle className="sr-only">Add Payment Method</DialogTitle>

                {/* Premium header */}
                <div className={`relative overflow-hidden rounded-t-2xl border-b ${isDark ? "border-gray-800" : "border-line"}`}>
                    {isDark ? (
                        <>
                            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black" />
                            <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-500/20 blur-3xl rounded-full" />
                            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-indigo-500/20 blur-3xl rounded-full" />
                            <div className="absolute top-0 left-0 w-full h-[1px] /10" />
                        </>
                    ) : (
                        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/50 to-white" />
                    )}

                    <div className="relative px-6 py-5">
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                {isDark && <div className="absolute inset-0 /10 blur-lg rounded-xl" />}
                                <div className={`relative w-11 h-11 rounded-xl flex items-center justify-center shadow-lg ${isDark
                                    ? "/10 backdrop-blur-xl border border-white/20"
                                    : " border border-line shadow-sm"
                                    }`}>
                                    <CreditCard className={`w-5 h-5 ${isDark ? "text-white" : "text-blue-600 dark:text-blue-300"}`} />
                                </div>
                            </div>
                            <div>
                                <h2 className={`text-lg font-semibold tracking-tight ${isDark ? "text-white" : "text-content"}`}>
                                    Add Payment Method
                                </h2>
                                <p className={`text-sm mt-0.5 ${isDark ? "text-gray-300" : "text-content-muted"}`}>
                                    Secure payment processing via Stripe
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <DialogBody className="px-7 pt-6 pb-2 space-y-6 max-h-[65vh] overflow-y-auto scrollbar-thin">

                    {/* ── Card Details ─────────────────────────────── */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                                Card Details
                            </h3>
                            <div className="flex gap-1.5">
                                {ACCEPTED_CARDS_CONFIG.slice(0, 4).map((card) => (
                                    <div
                                        key={card.id}
                                        className="w-9 h-6 bg-surface rounded flex items-center justify-center px-1 shadow-sm border border-line"
                                    >
                                        <img src={card.icon} alt={card.label} title={card.label} className="w-full h-full object-contain" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Card number */}
                        <div>
                            <label className="block text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-[6px]">
                                Card number
                            </label>
                            <div
                                className={stripeFocusCls("cardNumber")}
                                onFocus={() => setFocusedField("cardNumber")}
                                onBlur={() => setFocusedField(null)}
                            >
                                <CardNumberElement options={{ ...elementStyles, showIcon: true }} onChange={handleCardChange} />
                            </div>
                        </div>

                        {/* Expiry + CVC */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-[6px]">
                                    Expiration date
                                </label>
                                <div
                                    className={stripeFocusCls("expiry")}
                                    onFocus={() => setFocusedField("expiry")}
                                    onBlur={() => setFocusedField(null)}
                                >
                                    <CardExpiryElement options={elementStyles} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-[6px] flex items-center gap-1.5">
                                    CVC <Shield className="w-3.5 h-3.5 text-content-subtle" />
                                </label>
                                <div
                                    className={stripeFocusCls("cvc")}
                                    onFocus={() => setFocusedField("cvc")}
                                    onBlur={() => setFocusedField(null)}
                                >
                                    <CardCvcElement options={elementStyles} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr className="border-line dark:border-gray-800" />
                    <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                            Billing Address
                        </h3>
                        <AddressElement
                            options={{
                                mode: "billing",
                                defaultValues: {
                                    address: { country: "US" },
                                },
                                fields: {
                                    phone: "never",
                                },
                            }}
                        />
                    </div>

                    {/* Security badge */}
                    <div className="flex items-start gap-2.5 py-3 px-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-line/60 dark:border-gray-700/60">
                        <Lock className="w-4 h-4 text-content-muted dark:text-gray-400 flex-shrink-0 mt-0.5" />
                        <p className="text-ui leading-relaxed text-content-secondary dark:text-gray-400">
                            Your payment info is safely encrypted and processed by Stripe. We never store your full card details.
                        </p>
                    </div>
                </DialogBody>

                <DialogFooter className="px-7 pb-6 pt-4 gap-3 border-t border-line-subtle dark:border-gray-800 mt-2 bg-gray-50/50 dark:bg-black/20">
                    <Button onClick={() => onClose()} variant="secondary" className="flex-1 py-2.5">
                        Cancel
                    </Button>
                    <Button
                        onClick={createStripeCardToken}
                        disabled={loading}
                        className="flex-[2] py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm"
                    >
                        {loading ? (
                            <span className="flex items-center justify-center gap-2">
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Processing...
                            </span>
                        ) : (
                            <span className="flex items-center justify-center gap-2">
                                <Lock className="w-4 h-4" />
                                Securely Add Card
                            </span>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---- Export wrapped with Stripe Elements ----
export const AddPaymentCard: React.FC<AddPaymentMethodModalProps> = (props) => {
    const { isDark } = useTheme();
    return (
        <Elements
            stripe={stripePromise}
            options={{
                appearance: {
                    theme: isDark ? "night" : "stripe",
                    variables: {
                        colorPrimary: "#2563eb",
                        colorBackground: isDark ? "#1f2937" : "#ffffff",
                        colorText: isDark ? "#f3f4f6" : "#111827",
                        colorDanger: "#ef4444",
                        fontFamily: "'Inter', system-ui, sans-serif",
                        borderRadius: "8px",
                        spacingUnit: "4px",
                        spacingGridRow: "16px",
                        spacingGridColumn: "16px",
                    },
                    rules: {
                        ".Input": {
                            border: isDark ? "1px solid #374151" : "1px solid #d1d5db",
                            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                            padding: "12px",
                            fontSize: "14px",
                        },
                        ".Input:focus": {
                            border: "1px solid #2563eb",
                            boxShadow: "0 0 0 1px #2563eb",
                            outline: "none",
                        },
                        ".Input::placeholder": {
                            color: isDark ? "#6b7280" : "#9ca3af",
                        },
                        ".Label": {
                            fontWeight: "500",
                            fontSize: "13px",
                            color: isDark ? "#d1d5db" : "#374151",
                            marginBottom: "6px",
                        },
                        ".Error": {
                            marginTop: "6px",
                            fontSize: "12px",
                        },
                        ".Select": {
                            border: isDark ? "1px solid #374151" : "1px solid #d1d5db",
                            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                            padding: "12px",
                            fontSize: "14px",
                        },
                        ".Select:focus": {
                            border: "1px solid #2563eb",
                            boxShadow: "0 0 0 1px #2563eb",
                        },
                    },
                },
            }}
        >
            <AddPaymentMethodInner {...props} />
        </Elements>
    );
};
